import {getMomentFromCanvasTimelineDate} from './canvas-timeline/canvas_timeline_util';

/**
 * Syncs a phase's domain date fields to a pair of canvas day integers.
 *
 * The phase hover box (canvas_projects_scheduling_distribution_box) renders a phase's
 * startYear/startMonth/startDay and deadlineYear/deadlineMonth/deadlineDay, while the bar
 * is drawn from the phase item's canvas dates. Any mover that shifts a phase item's canvas
 * dates must call this too, or the hover box keeps the old range until a full refetch —
 * the same two-copies defect syncAllocationDatesToCanvasDates guards against for allocations.
 *
 * Start and deadline are converted independently: with "hide weekends" active the two ends
 * of an item receive different shifts. The numeric *Month fields are 1-based, matching
 * the other writers of these fields.
 *
 * Mutates and returns the phase.
 *
 * @param {object} phase a phase record from DataManager
 * @param {number} canvasStartDate canvas day integer for the new start
 * @param {number} canvasEndDate canvas day integer for the new deadline
 * @returns {object} the same phase, mutated
 */
export const syncPhaseDatesToCanvasDates = (phase, canvasStartDate, canvasEndDate) => {
	const startDate = getMomentFromCanvasTimelineDate(canvasStartDate);
	const endDate = getMomentFromCanvasTimelineDate(canvasEndDate);

	phase.startYear = startDate.year();
	phase.startMonth = startDate.month() + 1;
	phase.startDay = startDate.date();
	phase.deadlineYear = endDate.year();
	phase.deadlineMonth = endDate.month() + 1;
	phase.deadlineDay = endDate.date();

	return phase;
};

/**
 * After a project move has shifted every child item on the canvas, sync each phase record
 * from its own (already shifted) phase item. A phase without a drawn item is left alone —
 * it was not moved locally either, and the next refetch brings the server's value.
 *
 * @param {Array<object|null|undefined>} phases phase records (null entries are skipped)
 * @param {Array<object>} phaseItems canvas phase items, each carrying data.phase
 */
export const syncPhasesToPhaseItems = (phases, phaseItems) => {
	for (const phase of phases) {
		if (!phase) continue;

		const phaseItem = phaseItems.find(candidate => candidate.data?.phase?.id === phase.id);

		if (phaseItem) {
			syncPhaseDatesToCanvasDates(phase, phaseItem.startDate, phaseItem.endDate);
		}
	}
};
